/* eslint-disable no-console */
import useAppStore from '@/stores/useAppStore';
// eslint-disable-next-line import/no-cycle
import useAuthStore from '@/stores/useAuthStore';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { onMounted } from 'vue';
import useNotifyStore from '@/stores/useNotifyStore';
import errorService from '@/services/errorService';

const logError = (error) => {
  let errorsString = '';
  let errorStatus = '';
  try {
    if (error?.data?.errors) {
      // errors from DB
      Object.keys(error.data.errors).forEach((key) => {
        errorsString += `${key}: ${error.data.errors[key].join(', ')}, `;
      });
      errorsString = errorsString.slice(0, -2); // remove the extra ", " at the end
      errorStatus = error.status;
    } else if (error?.data?.message && error?.data?.stack) {
      // errors in code
      errorStatus = 0;
      errorsString = `${error?.data?.message}, ${error?.data?.stack}`;
    } else if (error?.message && error?.stack) {
      // errors in code
      errorStatus = 0;
      errorsString = `${error?.message}, ${error?.stack}`;
    } else if (typeof error?.data === 'string') {
      errorStatus = 0;
      errorsString = error?.data;
    } else {
      // others: stringify everything
      errorStatus = 0;
      errorsString = JSON.stringify(error);
    }
  } catch (ex) {
    errorStatus = 0;
    errorsString = JSON.stringify(error);
  }
  errorService.log(errorStatus, errorsString);
};

export default () => {
  let i18n = {};
  onMounted(() => {
    i18n = useI18n();
  });

  const get = (error) => {
    const { response, code } = error;
    if (error?.response?.request?.status === 401 || error?.response?.status === 401) {
      return { status: 401, data: null };
    }
    if (error?.response?.request?.status === 404) {
      return { status: 404, data: null };
    }
    if (code === 'ECONNABORTED') {
      return { status: 499, data: null };
    }
    if (typeof response === 'undefined') {
      const defaultReturn = { status: 500, data: error };
      if (defaultReturn?.data) {
        return { status: -1, data: 'Radās neapstrādāta kļūda' };
      }
      return defaultReturn;
    }
    const { request, ...errorObject } = response;
    const { data } = errorObject;

    if (
      Math.floor(errorObject.status / 100) === 4 &&
      data?.errors &&
      Array.isArray(data?.errors) &&
      data.errors.length > 0
    ) {
      const messages = data.errors.map((e) => (e.message || '').replace(/(\[.*\])/g, ''));
      return { status: 422, data: messages.join('\n') };
    }

    // A message from DB
    if (error?.response?.data?.error?.code.indexOf('DB') > -1) {
      return { status: 0, data: error?.response?.data?.error?.message };
    }

    if (errorObject.status === 422) {
      if (data?.error?.message) {
        return { status: 422, data: data?.error?.message };
      }
    }

    return {
      status: errorObject.status || 500,
      data: errorObject.data || null,
    };
  };

  const push = async (error) => {
    const router = useRouter();
    const authStore = useAuthStore();
    const appStore = useAppStore();
    const err = get(error);

    if (err.status === 401) {
      authStore.$reset();
      if (router) {
        router.push({ name: 'home' });
        return;
      }
    }
    let message = null;

    if (err.status === 401) {
      message = i18n.t('pages.sessionEnded.title');
    } else if (err.status === 403) {
      message = i18n.t('errors.forbidden');
    } else if (err.status === 405) {
      message = i18n.t('errors.notAllowed');
      return;
    } else if (err.status === 422) {
      message = i18n.t('errors.validation');
      return;
    } else if (err.status === 499) {
      message = i18n.t('errors.loadFailed');
      return;
    } else if (err.status === 404) {
      if (router) {
        await router.replace({ name: 'notFound' });
      } else {
        message = i18n.t('errors.http404');
      }
    } else if (err.status >= 500) {
      message = i18n.t(`errors.http${err.status}`);
    }

    if (err.status !== 401 && error?.request?.responseURL.indexOf('idauth') === -1) {
      logError(err);
    }

    // Tikai, ja error nesatur json
    if (err.data !== null && typeof err.data !== 'object') {
      appStore.error = err.data;
      appStore.showError = true;
    } else if (message !== null) {
      appStore.error = message;
      appStore.showError = true;
    }
  };

  return {
    get,
    push,
    pushWithNotification: async (error, defaultMessage) => {
      if (
        window?.config?.environment === 'Development' ||
        window?.config?.environment === 'Staging' ||
        window?.config?.environment === 'local'
      )
        console.error(error);

      const notifications = useNotifyStore();
      const err = get(error);
      const timeoutErrText = 'Exception while reading from stream';

      if (err.status === 401) {
        const router = useRouter();
        if (router) {
          await router.replace({ name: 'unauthorized' });
        }
      }
      // Tikai, ja nav DB atgriezts kļūdas teksts
      if (
        (err.status >= 500 || err.status === 401 || err.status === 403 || err.status === 404) &&
        (typeof err?.data?.error?.code.indexOf('DB') === 'undefined' ||
          err?.data?.error?.code.indexOf('DB') === -1) &&
        err?.data?.error?.message !== timeoutErrText
      ) {
        await push(error);
        return false;
      }

      // Nelogo no DB saņemtas apstrādātas kļūdas & idauth requestus
      if (err.status !== 0 && error?.request?.responseURL.indexOf('idauth') === -1) {
        logError(error);
      }

      const { '': errors } = (err.data || {}).errors || {};
      let [message] = errors || [];
      let description = '';

      if (err?.data?.error?.message === timeoutErrText) {
        message = i18n.t('errors.timeout');
      } else if (err.status === 304) {
        message = i18n.t(`errors.http${err.status}`);
      } else if (err.status === 422) {
        message = i18n.t(`errors.http${err.status}`);
        description = err.data.code ? i18n.t(`errors.${err.data.code}`) : err.data;
      } else if (err.status === 400) {
        message = i18n.t(`errors.http${err.status}`);
      } else if (err.status === 0) {
        // DB kļūda
        message = i18n.t(`errors.http400`);
        description = err.data;
      } else if (err.status === -1) {
        // Koda kļūda
        message = i18n.t(`errors.http500`);
        description = err.data;
      } else if (!message) {
        message = defaultMessage;
      }
      if (description) {
        notifications.pushError(message, description);
      } else {
        notifications.pushError(message);
      }
      return true;
    },
  };
};
